import { parseInput } from '../formatters/date-formatters';

/**
 * Calculates age from a date of birth Accounts for month and day to give accurate age in years Includes error handling
 * and validation
 *
 * @param dateOfBirth - Date of birth as string (ISO format) or Date object, or null
 * @returns Age in years as integer (0 if invalid or no date provided)
 */
export function calculateAge(dateOfBirth: string | Date | null | undefined): number | null {
  const birthDate = parseInput(dateOfBirth);
  if (!birthDate) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getUTCFullYear();
  const monthDiff = today.getMonth() - birthDate.getUTCMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
    age--;
  }

  return Math.max(0, age);
}

/**
 * Gets current age from a date of birth Convenience wrapper for calculateAge
 *
 * @example
 *   getCurrentAge('1990-05-15'); // Returns 33
 *
 * @param dateOfBirth - Date of birth
 * @returns Current age in years
 */
export function getCurrentAge(dateOfBirth: string | Date): number {
  return calculateAge(dateOfBirth);
}

/**
 * Validates if a date of birth is valid and reasonable Checks: not in future, age between 0-150 years, valid date
 * format
 *
 * @example
 *   isValidDateOfBirth('1990-05-15'); // Returns true
 *   isValidDateOfBirth(new Date()); // Returns false (today's date is invalid)
 *   isValidDateOfBirth('2100-05-15'); // Returns false (future date)
 *
 * @param dateOfBirth - Date of birth to validate
 * @returns True if valid, false otherwise
 */
export function isValidDateOfBirth(dateOfBirth: string | Date | null | undefined): boolean {
  if (!dateOfBirth) return false;

  try {
    const dob = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : (dateOfBirth as Date);

    if (isNaN(dob.getTime())) return false;

    const today = new Date();

    // Date must be in the past
    if (dob >= today) return false;

    // Age must be reasonable (0-150 years)
    const age = calculateAge(dob);
    return age >= 0 && age <= 150;
  } catch (error) {
    console.error('Error validating dateOfBirth:', dateOfBirth, error);
    return false;
  }
}
